import { Button, Input, Tooltip } from '@enterprise-ui/canvas-ui-react'
import React, { useEffect, useState } from 'react'
import { StyledSurveyForm, StyledSurveyTimer } from './styles'
import EnterpriseIcon, {
  ArrowRightIcon,
  CheckCircleFilledIcon,
  ClockIcon,
  InfoIcon,
  MinimizeIcon,
  InfoFilledIcon,
} from '@enterprise-ui/icons'
import useFeedbackSurveyResponse from '../../domain/useFeedbackSurveyResponse'
import useFeedbackSurveyInfo from '../../domain/useFeedbackSurveyInfo'
import moment from 'moment'
import useSkipFeedbackSurvey from '../../domain/useSkipFeedbackSurvey'
import useCreateFeedbackSurvey from '../../domain/useCreateFeedbackSurvey'
import useUpdateFeedbackSurvey from '../../domain/useUpdateFeedbackSurvey'

type QuestionType = 'SINGLE_SELECTION' | 'RATING' | 'TEXT' | 'MULTI_SELECTION'

interface Question {
  id: string
  type: QuestionType
  label: string
  options?: (string | number)[] | null
  required: boolean
  default_show_comment?: boolean | null
  comment_placeholder?: string | null
  placeholder?: string | null
  comment_mandate_value?: number | null
  error_message?: string | null
}

interface Step {
  id: number
  title?: string
  description?: string
  questions: Question[]
  resume_title?: string | null
  resume_description?: string | null
  error_message?: string | null
}

interface SurveyData {
  id: string
  survey_id: string
  title: string
  description: string
  active: boolean
  max_skips: number
  steps: Step[]
  created_by: string
  created_date: string
  user_category: string
  start_date: string
  end_date: string
  excluded_users: string[]
  skip_intervals: number[]
}

interface Responses {
  steps?: {
    [stepId: string]: {
      title?: string
      questions: {
        [questionId: string]: {
          question: string
          type: string
          answer: string | number | (string | number)[]
          comment?: string
        }
      }
    }
  }
  [key: string]: any
}

interface Errors {
  [key: string]: string | null
}

const SurveyForm = () => {
  const convertStringToArray = (value: string): (string | number)[] => {
    return value ? value.split(',').map((item: string) => item.trim()) : []
  }

  const convertArrayToString = (value: (string | number)[]): string => {
    return Array.isArray(value) ? value.join(',') : ''
  }

  const ensureMultiSelectArray = (
    value: any,
    questionType: string,
  ): (string | number)[] => {
    if (questionType !== 'MULTI_SELECTION') return value

    if (Array.isArray(value)) return value
    if (typeof value === 'string' && value.trim() !== '') {
      return convertStringToArray(value)
    }
    return []
  }

  const [stepIndex, setStepIndex] = useState<number>(0)
  const [responses, setResponses] = useState<Responses>({})
  const [errors, setErrors] = useState<Errors>({})
  const [showSurveyForm, setShowSurveyForm] = useState<boolean>(false)
  const [showSurveyTimer, setShowSurveyTimer] = useState<boolean>(false)
  const [timeLeft, setTimeLeft] = useState<string>('')
  const [showFinalStep, setShowFinalStep] = useState<boolean>(false)
  const [userRequestedShow, setUserRequestedShow] = useState<boolean>(false)
  const { data: surveyData = {} }: any = useFeedbackSurveyInfo()
  const {
    data: apiResponse = {},
    isLoading: isApiResponseLoading,
    isFetching: isApiResponseFetching,
  }: any = useFeedbackSurveyResponse(
    surveyData?.active || false,
    surveyData?.survey_id || '',
  )
  const { mutateAsync: skipFeedbackSurvey } = useSkipFeedbackSurvey()
  const { mutateAsync: createFeedbackSurvey } = useCreateFeedbackSurvey()
  const { mutateAsync: updateFeedbackSurvey } = useUpdateFeedbackSurvey()
  const [timeDiff, setTimeDiff] = useState<number>(0)

  const currentStep = surveyData?.steps?.[stepIndex]

  useEffect(() => {
    const targetTime = moment().add(timeDiff, 'milliseconds')

    const updateTimer = () => {
      const now = moment()
      const diff = targetTime.diff(now)
      if (diff <= 0) {
        setTimeLeft('00:00')
        clearInterval(timerInterval)
        setShowSurveyForm(true)
        setShowSurveyTimer(false)
        setTimeDiff(0)
        return
      }

      const duration = moment.duration(diff)
      const minutes = Math.floor(duration.asMinutes())
      const seconds = duration.seconds()
      setTimeLeft(
        `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      )
    }

    const timerInterval = setInterval(updateTimer, 1000)
    updateTimer()

    return () => clearInterval(timerInterval)
  }, [timeDiff])

  const calculateTimeDiff = () => {
    const skippedTime = apiResponse?.skipped_time
    return moment
      .utc(skippedTime)
      .add(apiResponse?.skip_timer_duration || 0, 'minutes')
      .diff(moment().utc())
  }

  const handleSurveyVisibility = (timeDiff: number) => {
    if (apiResponse?.skipped_time) {
      if (timeDiff >= 0 && !userRequestedShow) {
        setShowSurveyForm(false)
        setShowSurveyTimer(true)
      } else {
        setShowSurveyForm(true)
        setShowSurveyTimer(false)
      }
    } else {
      if (isApiResponseLoading || isApiResponseFetching) {
        setShowSurveyForm(false)
        setShowSurveyTimer(false)
      } else {
        setShowSurveyForm(true)
        setShowSurveyTimer(false)
      }
    }
  }
  useEffect(() => {
    if (
      surveyData?.active &&
      apiResponse &&
      (!apiResponse?.is_submitted || showFinalStep)
    ) {
      const calculatedTimeDiff = calculateTimeDiff()
      setTimeDiff(calculatedTimeDiff)
      handleSurveyVisibility(calculatedTimeDiff)
    } else {
      setShowSurveyForm(false)
      setShowSurveyTimer(false)
    }
  }, [
    apiResponse,
    isApiResponseLoading,
    isApiResponseFetching,
    surveyData?.active,
    userRequestedShow,
  ])

  useEffect(() => {
    const transformedResponses = { ...(apiResponse?.answer_steps || {}) }

    Object.keys(transformedResponses).forEach((stepId) => {
      const step = transformedResponses[stepId]
      if (step?.answers) {
        Object.keys(step.answers).forEach((questionId) => {
          const questionAnswer = step.answers[questionId]
          if (questionAnswer?.question_type === 'MULTI_SELECTION') {
            questionAnswer.answer = ensureMultiSelectArray(
              questionAnswer.answer,
              questionAnswer.question_type,
            )
          }
        })
      }
    })

    setResponses(transformedResponses)
  }, [apiResponse?.answer_steps])

  const handleChange = (
    question: Record<string, any>,
    value: string | number | (string | number)[],
    isComment?: boolean,
  ) => {
    setResponses((prev) => {
      const updatedResponses = { ...prev }
      const stepId = currentStep?.id?.toString()

      if (!updatedResponses[stepId]) {
        updatedResponses[stepId] = {
          title: currentStep?.title,
          answers: {},
        }
      }

      if (!updatedResponses[stepId].answers) {
        updatedResponses[stepId].answers = {}
      }

      if (!updatedResponses[stepId].answers?.[question.id]) {
        updatedResponses[stepId].answers[question.id] = {
          question: question?.label,
          type: question?.type,
          answer: question.type === 'MULTI_SELECTION' ? [] : '',
          comment: '',
        }
      }

      if (isComment) {
        updatedResponses[stepId].answers[question.id].comment = value as string
      } else {
        if (question.type === 'MULTI_SELECTION') {
          const currentAnswers = ensureMultiSelectArray(
            updatedResponses[stepId].answers[question.id].answer,
            'MULTI_SELECTION',
          )

          const newAnswers = currentAnswers.includes(value as string | number)
            ? currentAnswers.filter((item) => item !== value)
            : [...currentAnswers, value as string | number]

          updatedResponses[stepId].answers[question.id].answer = newAnswers
        } else {
          updatedResponses[stepId].answers[question.id].answer = value
        }
      }

      return updatedResponses
    })
    setErrors((prev) => ({ ...prev, [question.id]: null }))
  }

  const isValidResponse = (val: any): boolean => {
    return !(
      val === undefined ||
      val === null ||
      (Array.isArray(val) && val.length === 0) ||
      (typeof val === 'string' && val.trim() === '')
    )
  }

  const validateStep = (): boolean => {
    const newErrors: Errors = {}
    const stepId = currentStep?.id?.toString()
    const stepQuestions = responses?.[stepId]?.answers || {}
    const textRegex = /^[a-zA-Z0-9\s.,!?&'=()\-_]{0,500}$/

    currentStep?.questions?.forEach((q: any) => {
      const val = q?.comment_mandate_value || 0
      const questionData = stepQuestions[q.id]

      if (q.required && !isValidResponse(questionData?.answer)) {
        newErrors[q.id] = 'This field is required'
      }
      if (
        q.type === 'RATING' &&
        questionData?.answer < val &&
        !isValidResponse(questionData?.comment)
      ) {
        newErrors[q.id] = 'Comments field is required'
      }
      if (q.type === 'MULTI_SELECTION' && q.required) {
        const answerArray = ensureMultiSelectArray(
          questionData?.answer,
          'MULTI_SELECTION',
        )
        if (answerArray.length === 0) {
          newErrors[q.id] = 'Please select at least one option'
        }
      }
      if (
        q.type === 'TEXT' &&
        questionData?.answer &&
        !textRegex.test(questionData?.answer as string)
      ) {
        newErrors[q.id] =
          "Only letters, numbers, spaces, and basic punctuation (.,!?()-&'=_) are allowed. Maximum 500 characters."
      }
      if (
        questionData?.comment &&
        !textRegex.test(questionData?.comment as string)
      ) {
        newErrors[q.id + '_comment'] =
          "Only letters, numbers, spaces, and basic punctuation (.,!?()-&'=_) are allowed. Maximum 500 characters."
      }
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const areAllQuestionsAnswered = (): boolean => {
    const stepId = currentStep?.id?.toString()
    const stepQuestions = responses?.[stepId]?.answers || {}

    return (
      currentStep?.questions.every((q: any) => {
        const val = q?.comment_mandate_value || 0
        const questionData = stepQuestions[q.id]

        if (q.required) {
          if (q.type === 'MULTI_SELECTION') {
            const answerArray = ensureMultiSelectArray(
              questionData?.answer,
              'MULTI_SELECTION',
            )
            return answerArray.length > 0
          }
          if (q.type === 'RATING' && questionData?.answer < val) {
            return isValidResponse(questionData?.comment)
          }
          return isValidResponse(questionData?.answer)
        }
        return true
      }) || false
    )
  }

  const buildSurveyResponseObject = () => {
    const stepId = currentStep?.id?.toString()
    const stepQuestions = responses?.[stepId]?.answers || {}

    const transformedAnswers: { [key: string]: any } = {}

    currentStep?.questions.forEach((q: any) => {
      const questionData = stepQuestions[q.id]
      if (questionData) {
        let answerValue = questionData.answer

        if (q.type === 'MULTI_SELECTION' && Array.isArray(answerValue)) {
          answerValue = convertArrayToString(answerValue as (string | number)[])
        }

        transformedAnswers[q.id] = {
          question: questionData.question,
          question_type: questionData.type,
          answer: answerValue,
          comment: questionData.comment || '',
        }
      } else {
        const defaultAnswer = q.type === 'MULTI_SELECTION' ? [] : ''

        transformedAnswers[q.id] = {
          question: q.label,
          question_type: q.type,
          answer: defaultAnswer,
          comment: '',
        }
      }
    })

    return {
      survey_id: surveyData?.survey_id,
      current_step: stepIndex + 1,
      is_submitted: false,
      answer_steps: {
        [stepId]: {
          title: currentStep?.title,
          answers: transformedAnswers,
        },
      },
    }
  }

  const renderQuestion = (question: Question) => {
    const error = errors[question.id]
    const stepId = currentStep?.id?.toString()
    const questionData = responses?.[stepId]?.answers?.[question.id]

    const renderSingleSelectOptions = (
      options: (string | number)[],
      selectedValue: any,
      onSelect: (value: any) => void,
    ) => (
      <div className="option-contianer">
        {options.map((opt) => (
          <Button
            key={opt}
            size="expanded"
            className={`${selectedValue === opt ? 'selected' : ''}`}
            onClick={() => onSelect(opt)}
          >
            {opt}
          </Button>
        ))}
        {error && <div className="question-error-message">{error}</div>}
      </div>
    )

    const renderMultiSelectOptions = (
      options: (string | number)[],
      selectedValues: any[],
      onSelect: (value: any) => void,
    ) => (
      <div className="option-contianer">
        {options.map((opt) => {
          const safeSelectedValues = Array.isArray(selectedValues)
            ? selectedValues
            : []
          const isSelected = safeSelectedValues.some(
            (value) => String(value).trim() === String(opt).trim(),
          )
          return (
            <Button
              key={opt}
              size="expanded"
              className={`${isSelected ? 'selected' : ''}`}
              onClick={() => onSelect(opt)}
            >
              {opt}
            </Button>
          )
        })}
        {error && <div className="question-error-message">{error}</div>}
      </div>
    )

    switch (question.type) {
      case 'RATING':
      case 'SINGLE_SELECTION':
        return renderSingleSelectOptions(
          question.options || [],
          questionData?.answer,
          (value) => handleChange(question, value),
        )
      case 'MULTI_SELECTION': {
        const selectedValues = ensureMultiSelectArray(
          questionData?.answer,
          'MULTI_SELECTION',
        )

        return renderMultiSelectOptions(
          question.options || [],
          selectedValues,
          (value) => handleChange(question, value),
        )
      }
      case 'TEXT':
        return (
          <div>
            <Input.Textarea
              id={question.id}
              className="textarea-question"
              placeholder={question.placeholder}
              value={(questionData?.answer as string) || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleChange(question, e.target.value)
              }
            />
            {error && <div className="question-error-message">{error}</div>}
          </div>
        )
      default:
        return null
    }
  }

  const handleNext = async () => {
    if (validateStep()) {
      try {
        const surveyResponseData = buildSurveyResponseObject()
        if (apiResponse && apiResponse?.survey_id) {
          await updateFeedbackSurvey(surveyResponseData)
        } else {
          await createFeedbackSurvey(surveyResponseData)
        }

        setStepIndex(stepIndex + 1)
      } catch (error) {
        console.error('Error saving survey response:', error)
      }
    }
  }

  const handleSubmit = async () => {
    if (validateStep()) {
      try {
        const surveyResponseData = buildSurveyResponseObject()
        surveyResponseData.is_submitted = true
        if (apiResponse && apiResponse?.survey_id) {
          await updateFeedbackSurvey(surveyResponseData)
        } else {
          await createFeedbackSurvey(surveyResponseData)
        }

        setStepIndex(stepIndex + 1)
        setShowFinalStep(true)
      } catch (error) {
        console.error('Error submitting survey:', error)
      }
    }
  }

  const showFeedbackSkippedSurvey = (show: boolean) => {
    setShowSurveyForm(show)
    setShowSurveyTimer(!show)
    setStepIndex(0)
    if (show) {
      setUserRequestedShow(true)
    } else {
      setUserRequestedShow(false)
    }
  }

  const renderStartingStepBody = () => {
    if (apiResponse?.current_step > 0) {
      return (
        <>
          <h1>{currentStep?.resume_title}</h1>
          <p>
            {currentStep?.resume_description}{' '}
            <Button type="secondary" disabled>
              <EnterpriseIcon icon={ClockIcon} size="inline" /> Takes under 2
              minutes
            </Button>
          </p>
          <Button
            onClick={() => setStepIndex(apiResponse?.current_step)}
            type="primary"
            size="expanded"
          >
            Resume
          </Button>
        </>
      )
    }

    return (
      <>
        <h1>{currentStep?.title}</h1>
        <Button type="secondary" disabled>
          <EnterpriseIcon icon={ClockIcon} size="inline" /> Takes under 3
          minutes
        </Button>
        <p>{currentStep?.description}</p>
        <Button onClick={() => setStepIndex(1)} type="primary" size="expanded">
          Get Started
        </Button>
      </>
    )
  }

  const handleSkipInterval = async (interval: number) => {
    skipFeedbackSurvey({
      surveyId: surveyData?.survey_id,
      skipTimerDuration: interval,
    })
    setShowSurveyForm(false)
  }

  const renderStartingStepFooter = () => (
    <Button
      type="ghost"
      className="btn-remind-later"
      onClick={() => {
        handleSkipInterval(surveyData?.skip_intervals?.[0] || 15)
        setUserRequestedShow(false)
      }}
    >
      Remind me later
    </Button>
  )

  const renderFinalStep = () => (
    <div className="final-step">
      <div className="final-step-icon">
        <EnterpriseIcon icon={CheckCircleFilledIcon} size="inline" />
      </div>
      <h2>{currentStep?.title}</h2>
      <p>{currentStep?.description}</p>
      <Button
        onClick={() => setShowSurveyForm(false)}
        type="primary"
        size="expanded"
      >
        Close
      </Button>
    </div>
  )

  const renderQuestions = () => {
    const stepId = currentStep?.id?.toString()
    const stepQuestions = responses?.[stepId]?.answers || {}

    return currentStep?.questions.map((question: any) => (
      <div key={question.id} className="question-container">
        <label>
          {question.label}
          {question?.question_description && (
            <Tooltip
              content={question.question_description}
              className="question-tooltip"
              location="right"
            >
              <EnterpriseIcon icon={InfoIcon} size="md" className="info-icon" />
            </Tooltip>
          )}
        </label>
        {question.type === 'MULTI_SELECTION' && (
          <p className="question-sub-label">Select all that apply.</p>
        )}
        {renderQuestion(question)}
        {(question.default_show_comment ||
          (question.type === 'RATING' &&
            stepQuestions[question.id]?.answer)) && (
          <div>
            <Input.Textarea
              id={question.id + '_comment'}
              placeholder={
                stepQuestions[question.id]?.answer <
                (question.placeholder_change_mandate_value || 0)
                  ? question.comment_placeholder_for_less_score
                  : question.comment_placeholder
              }
              value={(stepQuestions[question.id]?.comment as string) || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleChange(question, e.target.value, true)
              }
            />
            {errors[question.id + '_comment'] && (
              <div className="question-error-message">
                {errors[question.id + '_comment']}
              </div>
            )}
            {question.type === 'RATING' &&
              stepQuestions[question.id]?.answer &&
              stepQuestions[question.id]?.answer <
                (question.comment_mandate_value || 0) &&
              question?.error_message && (
                <div className="question-hint-message">
                  <EnterpriseIcon
                    icon={InfoFilledIcon}
                    size="inline"
                    className="info-icon"
                  />{' '}
                  {question?.error_message}
                </div>
              )}
          </div>
        )}
      </div>
    ))
  }

  const renderStepActions = () => (
    <div className="step-action">
      {stepIndex > 1 && stepIndex < surveyData?.steps?.length - 1 && (
        <Button
          onClick={() => setStepIndex(stepIndex - 1)}
          className="step-action-start"
          size="expanded"
          type="secondary"
        >
          Back
        </Button>
      )}
      {stepIndex < surveyData?.steps?.length - 2 && (
        <Button
          onClick={handleNext}
          className="step-action-end"
          size="expanded"
          type="primary"
          disabled={!areAllQuestionsAnswered()}
        >
          Next <EnterpriseIcon icon={ArrowRightIcon} />
        </Button>
      )}
      {stepIndex === surveyData?.steps?.length - 2 && (
        <Button
          onClick={handleSubmit}
          className="step-action-end"
          size="expanded"
          type="primary"
          disabled={!areAllQuestionsAnswered()}
        >
          Submit <EnterpriseIcon icon={ArrowRightIcon} />
        </Button>
      )}
    </div>
  )

  const renderStep = () => {
    if (currentStep?.id === '0') {
      return (
        <div className="starting-step-container">
          <div className="starting-step-body">{renderStartingStepBody()}</div>
          <div className="starting-step-footer">
            {renderStartingStepFooter()}
          </div>
        </div>
      )
    }

    if (currentStep?.id === '5') {
      return renderFinalStep()
    }

    return (
      <div className="steps-container">
        <span className="steps-count">
          Step {stepIndex}/{surveyData?.steps?.length - 2}
        </span>
        {currentStep?.title && (
          <h3 className="step-title">{currentStep.title}</h3>
        )}
        {currentStep?.description && (
          <p className="step-desc">{currentStep.description}</p>
        )}
        {renderQuestions()}
        {renderStepActions()}
      </div>
    )
  }
  return (
    <>
      {showSurveyTimer && (
        <StyledSurveyTimer>
          <div className="survey-timer-content">
            <span>
              <Button
                type="primary"
                onClick={() => showFeedbackSkippedSurvey(true)}
              >
                Show Feedback Survey
              </Button>
              <EnterpriseIcon icon={ClockIcon} size="inline" />
              <span className="survey-timer">
                We’ll remind you again in :<strong>{timeLeft}</strong>
              </span>
            </span>
          </div>
        </StyledSurveyTimer>
      )}
      <StyledSurveyForm headingText="" isVisible={showSurveyForm}>
        {renderStep()}
        {!(currentStep?.id === '0' || currentStep?.id === '5') && (
          <span
            className="survey-minimize-button"
            onClick={() => {
              handleSkipInterval(surveyData?.skip_intervals?.[0] || 15)
              setUserRequestedShow(false)
            }}
          >
            <EnterpriseIcon icon={MinimizeIcon} />
          </span>
        )}
      </StyledSurveyForm>
    </>
  )
}
export default SurveyForm
